import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    signOut
} from 'firebase/auth';
import { auth } from '../firebase/config';

// Only this Google account may access the seller admin panel
const ADMIN_EMAIL = 'adititextile2026@gmail.com';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (!context) {
        throw new Error('useAdminAuth must be used within an AdminAuthProvider');
    }
    return context;
};

export const AdminAuthProvider = ({ children }) => {
    const [adminUser, setAdminUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setAdminUser(firebaseUser?.email === ADMIN_EMAIL ? firebaseUser : null);
            setCheckingAuth(false);
        });
        return unsubscribe;
    }, []);

    // Seller signs in with the allow-listed Google account
    const login = async () => {
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            if (result.user.email !== ADMIN_EMAIL) {
                await signOut(auth);
                return { success: false, error: 'This Google account is not authorized for seller access.' };
            }
            return { success: true };
        } catch (err) {
            return { success: false, error: `Google sign-in failed (${err.code || err.message}). Please try again.` };
        }
    };

    const logout = () => signOut(auth);

    const value = useMemo(
        () => ({
            adminUser,
            isAdmin: !!adminUser,
            checkingAuth,
            login,
            logout
        }),
        [adminUser, checkingAuth]
    );

    return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};
